import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Shield, TrendingUp, Users, Activity, DollarSign, Settings,
  AlertTriangle, CheckCircle, Clock, MapPin, Zap, Award,
  RefreshCw, ChevronUp, ChevronDown, ArrowRight, Bell,
  BarChart2, FileText, Target, Brain, Star, Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ComposedChart,
  Line, RadialBarChart, RadialBar, Cell, PieChart, Pie
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LTip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import CountUp from 'react-countup';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const API = (import.meta as any).env?.VITE_API_URL ||
  'https://nexus360-platform-backend-api-production.up.railway.app';
const REGION_COORDS: Record<string,[number,number]> = {
  'Adamaoua':[7.3167,13.5833],'Centre':[3.8667,11.5167],'Est':[4.5833,13.6833],
  'Extrême-Nord':[10.5958,14.3249],'Littoral':[4.0511,9.7679],'Nord':[9.3,13.4],
  'Nord-Ouest':[5.9597,10.1459],'Ouest':[5.4767,10.4175],'Sud':[2.9,11.15],
  'Sud-Ouest':[4.6333,9.4333],
};
const REGIONS = Object.keys(REGION_COORDS);
const PAL = ['#3b82f6','#10b981','#f97316','#a855f7','#eab308','#ef4444','#06b6d4','#ec4899','#84cc16','#f43f5e'];

// ── Types ─────────────────────────────────────────────────────────────────────
interface Incident { incident_id:string; region:string; incident_type:string; severity:number; status:string; response_time_minutes:number; incident_date:string; city:string; }
interface Asset { asset_id:string; region:string; asset_type:string; engine_temp_warning:string; oil_pressure_warning:string; last_maintenance_date:string; purchase_cost:number; }
interface Training { training_id:string; region:string; program_type:string; participants_count:number; completions_count:number; pre_training_score:number; post_training_score:number; total_cost:number; start_date:string; }
interface Improvement { improvement_id:string; region:string; process_name:string; savings_realized_fcfa:number; quality_impact_score:number; baseline_time_hours:number; improved_time_hours:number; }

// ── Helpers ───────────────────────────────────────────────────────────────────
const n = (v:any,fb=0)=>{const x=Number(v);return isNaN(x)?fb:x;};
const fm = (v:number)=>v>=1e9?(v/1e9).toFixed(1)+'B':v>=1e6?(v/1e6).toFixed(1)+'M':v>=1e3?(v/1e3).toFixed(0)+'K':String(Math.round(v));
const ff = (v:number)=>fm(v)+' F';
const pct = (a:number,b:number)=>b>0?Math.round(a/b*1000)/10:0;
const isResolved = (i:Incident)=>['Résolu','Clôturé'].includes(i.status);
const isCritical = (i:Incident)=>n(i.severity)===1;
const bl = (v:any):boolean=>v===true||String(v).toLowerCase()==='true'||v==='1';
const isOpAvail = (a:Asset)=>!bl(a.engine_temp_warning)&&!bl(a.oil_pressure_warning);

const computeNSI = (incidents:Incident[],assets:Asset[],trainings:Training[]) => {
  if(!incidents.length) return 0;
  const resRate=pct(incidents.filter(isResolved).length,incidents.length);
  const critRate=pct(incidents.filter(isCritical).length,incidents.length);
  const assetAvail=assets.length?pct(assets.filter(isOpAvail).length,assets.length):75;
  const certRate=trainings.length?pct(trainings.reduce((s,t)=>s+n(t.completions_count),0),trainings.reduce((s,t)=>s+n(t.participants_count),0)):70;
  const avgResp=incidents.length?incidents.reduce((s,i)=>s+n(i.response_time_minutes),0)/incidents.length:60;
  const respScore=Math.max(0,100-avgResp/1.2);
  const raw=resRate*0.30+(100-critRate)*0.25+assetAvail*0.20+certRate*0.15+respScore*0.10;
  return Math.round(raw*10)/10;
};

// ── WOW: Skeleton ─────────────────────────────────────────────────────────────
const SkeletonCard=()=>(
  <div className="rounded-xl border border-blue-900/40 bg-slate-800/80 p-4">
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={14} width="55%" className="mb-3"/>
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={28} width="35%" className="mb-2"/>
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={10} count={2}/>
  </div>
);

// ── WOW: KPI Card ─────────────────────────────────────────────────────────────
const KPICard=({icon:Icon,label,numValue,suffix='',prefix='',color,bg,border,decimals=0,delta,alert=false}:
  {icon:any;label:string;numValue:number;suffix?:string;prefix?:string;color:string;bg:string;border:string;decimals?:number;delta:number;alert?:boolean})=>{
  const glowColor=color.includes('green')?'rgba(16,185,129,0.2)':color.includes('purple')?'rgba(168,85,247,0.2)':color.includes('orange')?'rgba(249,115,22,0.2)':color.includes('yellow')?'rgba(234,179,8,0.2)':color.includes('cyan')?'rgba(6,182,212,0.2)':'rgba(59,130,246,0.2)';
  return(
    <motion.div whileHover={{scale:1.05,boxShadow:`0 0 20px ${glowColor}`}} transition={{duration:.15}}
      className={`rounded-xl p-3 border ${border} ${bg} flex flex-col gap-2 cursor-default relative overflow-hidden`}
      style={{backdropFilter:'blur(8px)',background:'linear-gradient(135deg,rgba(15,23,42,0.85) 0%,rgba(30,41,59,0.6) 100%)'}}>
      <motion.div className="absolute top-0 left-0 right-0 h-px opacity-40"
        style={{background:`linear-gradient(90deg,transparent,${color.includes('green')?'#10b981':color.includes('purple')?'#a855f7':color.includes('orange')?'#f97316':color.includes('yellow')?'#eab308':color.includes('cyan')?'#06b6d4':'#3b82f6'},transparent)`}}
        animate={{x:['-100%','100%']}} transition={{duration:2.5,repeat:Infinity,ease:'linear',repeatDelay:1}}/>
      <div className="flex items-center justify-between">
        <Icon className={`w-4 h-4 ${color} ${alert?'animate-pulse':''}`}/>
        <motion.span className={`flex items-center gap-0.5 text-xs font-bold ${delta>=0?'text-green-400':'text-red-400'}`}
          initial={{opacity:0,x:4}} animate={{opacity:1,x:0}} transition={{delay:.5}}>
          {delta>=0?<ChevronUp className="w-3 h-3"/>:<ChevronDown className="w-3 h-3"/>}
          {Math.abs(delta)}%
        </motion.span>
      </div>
      <div>
        <p className={`font-bold text-4xl leading-none ${color}`}>
          <CountUp end={numValue} duration={1.5} decimals={decimals} prefix={prefix} suffix={suffix} preserveValue separator=" "/>
        </p>
        <p className="text-slate-400 text-xs mt-0.5">{label}</p>
      </div>
      {alert&&<motion.div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-400"
        animate={{opacity:[1,.2,1],scale:[1,1.5,1]}} transition={{duration:1,repeat:Infinity}}/>}
    </motion.div>
  );
};

// ── Tooltip ───────────────────────────────────────────────────────────────────
const NexTip=({active,payload,label}:any)=>{
  if(!active||!payload?.length)return null;
  return(<motion.div initial={{opacity:0,scale:.95}} animate={{opacity:1,scale:1}} transition={{duration:.12}}
    className="bg-slate-900/95 border border-slate-600 rounded-xl p-3 shadow-2xl text-xs backdrop-blur min-w-36">
    <p className="text-white font-semibold mb-1.5 pb-1 border-b border-slate-700">{label}</p>
    {payload.map((p:any,i:number)=>(<div key={i} className="flex justify-between gap-3 py-0.5"><div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{background:p.fill||p.stroke||p.color}}/><span className="text-slate-300">{p.name}</span></div><span className="font-bold" style={{color:p.fill||p.stroke||p.color}}>{p.value}</span></div>))}
  </motion.div>);
};

// ── Animated NSI Gauge ────────────────────────────────────────────────────────
const NSIGauge=({score}:{score:number})=>{
  const color=score>=80?'#10b981':score>=65?'#3b82f6':score>=50?'#eab308':'#ef4444';
  const label=score>=80?'EXCELLENT':score>=65?'BON':score>=50?'MODÉRÉ':'CRITIQUE';
  const r=80,cx=100,cy=105;
  const toR=(d:number)=>d*Math.PI/180;
  const px=(d:number)=>cx+r*Math.cos(toR(d));
  const py=(d:number)=>cy-r*Math.sin(toR(d));
  const needleDeg=180-(score/100)*180;
  const nx=cx+(r-18)*Math.cos(toR(needleDeg));
  const ny=cy-(r-18)*Math.sin(toR(needleDeg));
  return(
    <div className="flex flex-col items-center">
      <svg width="200" height="210" viewBox="0 0 200 210">
        <defs><linearGradient id="nsiGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#ef4444"/><stop offset="40%" stopColor="#eab308"/>
          <stop offset="70%" stopColor="#3b82f6"/><stop offset="100%" stopColor="#10b981"/>
        </linearGradient></defs>
        <path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(0)} ${py(0)}`} fill="none" stroke="#1e293b" strokeWidth="14" strokeLinecap="round"/>
        {score>0&&<motion.path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(needleDeg)} ${py(needleDeg)}`}
          fill="none" stroke="url(#nsiGrad)" strokeWidth="14" strokeLinecap="round"
          initial={{pathLength:0,opacity:0}} animate={{pathLength:1,opacity:1}} transition={{duration:2,ease:'easeOut'}}/>}
        {[0,25,50,75,100].map(v=>{
          const deg=180-(v/100)*180;
          const x1=cx+(r-20)*Math.cos(toR(deg)),y1=cy-(r-20)*Math.sin(toR(deg));
          const x2=cx+(r-7)*Math.cos(toR(deg)),y2=cy-(r-7)*Math.sin(toR(deg));
          return <line key={v} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#334155" strokeWidth="1.5"/>;
        })}
        <motion.line x1={cx} y1={cy} x2={cx} y2={cy-(r-18)}
          animate={{x2:nx,y2:ny}} transition={{type:'spring',stiffness:50,damping:15,delay:.6}}
          stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r="6" fill="white"/>
        <text x={cx} y={cy+58} textAnchor="middle" fill="white" fontSize="28" fontWeight="bold">{score.toFixed(0)}</text>
        <text x={cx} y={cy+86} textAnchor="middle" fill={color} fontSize="20" fontWeight="bold">{label}</text>
        <text x="14" y="118" fill="#ef4444" fontSize="8">0</text>
        <text x="178" y="118" fill="#10b981" fontSize="8">100</text>
      </svg>
      <p className="text-slate-400 text-xs mt-1">Indice de Sécurité Nationale</p>
    </div>
  );
};

// ── Alert Feed ────────────────────────────────────────────────────────────────
const AlertFeed=({incidents,assets}:{incidents:Incident[];assets:Asset[]})=>{
  const alerts=useMemo(()=>{
    const list:Array<{id:string;type:string;msg:string;level:string;time:string}>=[];
    const d30=Date.now()-30*24*3600*1000;
    incidents.filter(i=>isCritical(i)&&new Date(i.incident_date).getTime()>d30)
      .sort((a,b)=>new Date(b.incident_date).getTime()-new Date(a.incident_date).getTime())
      .slice(0,6).forEach(i=>{list.push({id:i.incident_id,type:'incident',msg:`P${i.severity} — ${i.incident_type} · ${i.city} (${i.region})`,level:'critical',time:i.incident_date.slice(0,10)});});
    return list;
  },[incidents,assets]);
  return(
    <div className="space-y-2">
      {alerts.map((a,i)=>(
        <motion.div key={a.id} initial={{opacity:0,x:8}} animate={{opacity:1,x:0}} transition={{delay:i*.05}}
          whileHover={{scale:1.01,boxShadow:'0 0 10px rgba(239,68,68,0.15)'}}
          className="flex items-start gap-2.5 p-2.5 rounded-lg border text-xs bg-red-900/20 border-red-900/40">
          <span className="relative flex h-3 w-3 flex-shrink-0 mt-0.5">
            <motion.span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-red-400"/>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-400"/>
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate text-red-300">{a.msg}</p>
            <p className="text-slate-500 mt-0.5">{a.time}</p>
          </div>
        </motion.div>
      ))}
      {alerts.length===0&&<p className="text-slate-500 text-xs text-center py-4">Aucune alerte active</p>}
    </div>
  );
};

// ── Region Performance ────────────────────────────────────────────────────────
const RegionPerf=({incidents,assets,trainings}:{incidents:Incident[];assets:Asset[];trainings:Training[]})=>{
  const data=useMemo(()=>REGIONS.map((r,i)=>{
    const ri=incidents.filter(x=>x.region===r),ra=assets.filter(x=>x.region===r),rt=trainings.filter(x=>x.region===r);
    const resRate=ri.length?pct(ri.filter(isResolved).length,ri.length):0;
    const assetAvail=ra.length?pct(ra.filter(isOpAvail).length,ra.length):0;
    const certRate=rt.length?pct(rt.reduce((s,t)=>s+n(t.completions_count),0),rt.reduce((s,t)=>s+n(t.participants_count),0)):0;
    const score=Math.round(resRate*0.40+assetAvail*0.30+certRate*0.30);
    const level=score>=75?'Bon':score>=55?'Moyen':'Faible';
    const color=score>=75?'#10b981':score>=55?'#eab308':'#ef4444';
    return{region:r.replace('Extrême-Nord','Ex-Nord').replace('Nord-Ouest','N-Ouest').replace('Sud-Ouest','S-Ouest'),score,resRate,assetAvail,certRate,level,color,incidents:ri.length,color2:PAL[i%PAL.length]};
  }).sort((a,b)=>b.score-a.score),[incidents,assets,trainings]);
  return(
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-blue-900/60 bg-slate-700">
            {['#','Région','Score','Résolution','Actifs opér.','Formation','Niveau'].map(h=>(
              <th key={h} className="px-3 py-2 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((r,i)=>(
            <motion.tr key={r.region} initial={{opacity:0}} animate={{opacity:1}} transition={{delay:i*.03}}
              whileHover={{backgroundColor:'rgba(59,130,246,0.05)'}}
              className={`border-b border-blue-900/30 transition-colors ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
              <td className="px-3 py-2 text-slate-400 font-bold">{i+1}</td>
              <td className="px-3 py-2 text-white font-medium">{r.region}</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <div className="w-16 bg-slate-700 rounded-full h-1.5 overflow-hidden">
                    <motion.div className="h-1.5 rounded-full" initial={{width:0}} animate={{width:`${r.score}%`}} transition={{duration:1,delay:i*.05}} style={{background:r.color}}/>
                  </div>
                  <span className="font-bold" style={{color:r.color}}>{r.score}</span>
                </div>
              </td>
              <td className="px-3 py-2 text-center"><span className={r.resRate>=70?'text-green-400':r.resRate>=50?'text-yellow-400':'text-red-400'}>{r.resRate}%</span></td>
              <td className="px-3 py-2 text-center"><span className={r.assetAvail>=70?'text-green-400':r.assetAvail>=50?'text-yellow-400':'text-red-400'}>{r.assetAvail}%</span></td>
              <td className="px-3 py-2 text-center"><span className={r.certRate>=70?'text-green-400':r.certRate>=50?'text-yellow-400':'text-red-400'}>{r.certRate}%</span></td>
              <td className="px-3 py-2">
                <motion.span className="px-2 py-0.5 rounded-full text-xs font-bold border"
                  animate={r.level==='Faible'?{opacity:[1,.6,1]}:{}} transition={{duration:1.5,repeat:Infinity}}
                  style={{color:r.color,background:`${r.color}22`,borderColor:`${r.color}55`}}>
                  {r.level}
                </motion.span>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Map ───────────────────────────────────────────────────────────────────────
const MapFix=()=>{const map=useMap();React.useEffect(()=>{setTimeout(()=>{try{map.invalidateSize();}catch(e){}},400);},[map]);return null;};
const CommandMap=({incidents,assets}:{incidents:Incident[];assets:Asset[]})=>{
  const regionStats=useMemo(()=>REGIONS.map(r=>{
    const ri=incidents.filter(i=>i.region===r),ra=assets.filter(a=>a.region===r);
    const critRate=ri.length?pct(ri.filter(isCritical).length,ri.length):0;
    const assetAlert=ra.filter(a=>bl(a.engine_temp_warning)||bl(a.oil_pressure_warning)).length;
    const riskScore=Math.min(100,Math.round(critRate*0.6+assetAlert*5));
    const color=riskScore>=60?'#ef4444':riskScore>=30?'#f97316':riskScore>=15?'#eab308':'#10b981';
    return{region:r,coords:REGION_COORDS[r],incidents:ri.length,critRate,assetAlert,riskScore,color};
  }),[incidents,assets]);
  return(
    <div className="relative rounded-xl overflow-hidden border border-blue-900/40" style={{height:'380px'}}>
      <style>{`.cmd-tip .leaflet-tooltip{background:transparent!important;border:none!important;box-shadow:none!important;padding:0!important}.cmd-tip .leaflet-tooltip::before{display:none!important}.leaflet-container{background:#0f172a!important}.leaflet-control-zoom a{background:#1e293b!important;color:#94a3b8!important;border-color:#334155!important}`}</style>
      <MapContainer center={[5.5,12.3]} zoom={6} style={{width:'100%',height:'100%'}} attributionControl={false} scrollWheelZoom>
        <MapFix/>
        <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" subdomains="abcd" maxZoom={19}/>
        {regionStats.map(r=>{
          const radius=Math.max(14,Math.min(36,14+(r.incidents/40)*22));
          return(
            <React.Fragment key={r.region}>
              <CircleMarker center={r.coords} radius={radius*1.6} pathOptions={{color:r.color,weight:.5,fillColor:r.color,fillOpacity:.08,dashArray:'3 3'}}/>
              <CircleMarker center={r.coords} radius={radius} pathOptions={{color:r.color,weight:2,fillColor:r.color,fillOpacity:.6}}>
                <LTip className="cmd-tip" direction="top" offset={[0,-radius]} opacity={1}>
                  <div style={{background:'#0a0f1e',border:`1.5px solid ${r.color}`,borderRadius:'12px',padding:'12px',minWidth:'180px',fontFamily:'system-ui,sans-serif',color:'white',boxShadow:`0 6px 24px ${r.color}66`}}>
                    <div style={{fontWeight:700,color:r.color,marginBottom:'8px',fontSize:'12px'}}>{r.region}</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px',fontSize:'11px'}}>
                      {[[r.color,r.riskScore,'Score risque'],['#3b82f6',r.incidents,'Incidents'],['#ef4444',r.critRate+'%','Critiques'],['#f97316',r.assetAlert,'Alertes actifs']].map(([col,val,lbl])=>(
                        <div key={String(lbl)} style={{background:'#1e293b',borderRadius:'6px',padding:'5px',textAlign:'center'}}>
                          <div style={{fontSize:'16px',fontWeight:'bold',color:String(col)}}>{val}</div>
                          <div style={{color:'#64748b',fontSize:'9px'}}>{lbl}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </LTip>
              </CircleMarker>
            </React.Fragment>
          );
        })}
      </MapContainer>
      <div className="absolute bottom-10 left-3 bg-slate-900/92 border border-blue-900/40 rounded-lg p-2 pointer-events-none" style={{zIndex:1000}}>
        {[['#ef4444','Risque élevé'],['#f97316','Risque moyen'],['#eab308','Risque faible'],['#10b981','Stable']].map(([c,l])=>(
          <div key={l} className="flex items-center gap-1.5 mb-0.5"><span className="w-2 h-2 rounded-full" style={{background:c}}/><span className="text-slate-300" style={{fontSize:'9px'}}>{l}</span></div>
        ))}
      </div>
    </div>
  );
};

// ── Glassmorphism card wrapper ────────────────────────────────────────────────
const GlassCard=({icon:Icon,title,accent='text-blue-400',children,className='',extra}:
  {icon:any;title:string;accent?:string;children:React.ReactNode;className?:string;extra?:React.ReactNode})=>(
  <motion.div whileHover={{boxShadow:'0 0 24px rgba(59,130,246,0.08)'}}
    className={`rounded-xl border border-blue-900/40 overflow-hidden shadow-lg ${className}`}
    style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
      style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
      <Icon className={`w-4 h-4 ${accent}`}/><span className="text-sm font-semibold text-white flex-1">{title}</span>{extra}
    </div>
    <div className="p-4">{children}</div>
  </motion.div>
);

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export const ExecutiveDashboardPage=({user}:{user:any})=>{
  const[incidents,setIncidents]=useState<Incident[]>([]);
  const[assets,setAssets]=useState<Asset[]>([]);
  const[trainings,setTrainings]=useState<Training[]>([]);
  const[improvements,setImprovements]=useState<Improvement[]>([]);
  const[loading,setLoading]=useState(true);
  const[lastUpd,setLastUpd]=useState(new Date());
  const[refreshing,setRefreshing]=useState(false);

  const fetchAll=useCallback(async()=>{
    setRefreshing(true);
    try{
      const[r1,r2,r3,r4]=await Promise.all([
        fetch(`${API}/api/incidents`).then(r=>r.json()).catch(()=>({data:[]})),
        fetch(`${API}/api/assets`).then(r=>r.json()).catch(()=>({data:[]})),
        fetch(`${API}/api/training/programs`).then(r=>r.json()).catch(()=>({data:[]})),
        fetch(`${API}/api/lean/improvements`).then(r=>r.json()).catch(()=>({data:[]})),
      ]);
      if(r1.data?.length)setIncidents(r1.data);
      if(r2.data?.length)setAssets(r2.data);
      if(r3.data?.length)setTrainings(r3.data.map((t:any)=>({...t,participants_count:n(t.participants_count),completions_count:n(t.completions_count),pre_training_score:n(t.pre_training_score),post_training_score:n(t.post_training_score),total_cost:n(t.total_cost)})));
      if(r4.data?.length)setImprovements(r4.data.map((i:any)=>({...i,savings_realized_fcfa:n(i.savings_realized_fcfa),quality_impact_score:n(i.quality_impact_score),baseline_time_hours:n(i.baseline_time_hours),improved_time_hours:n(i.improved_time_hours)})));
    }catch(e){console.error(e);}
    finally{setLoading(false);setRefreshing(false);setLastUpd(new Date());}
  },[]);
  useEffect(()=>{fetchAll();},[fetchAll]);
  useEffect(()=>{const t=setInterval(fetchAll,30000);return()=>clearInterval(t);},[fetchAll]);

  const nsi=useMemo(()=>computeNSI(incidents,assets,trainings),[incidents,assets,trainings]);
  const totalInc=incidents.length;
  const resolvedInc=incidents.filter(isResolved).length;
  const criticalInc=incidents.filter(isCritical).length;
  const resRate=pct(resolvedInc,totalInc);
  const avgResp=totalInc?Math.round(incidents.reduce((s,i)=>s+n(i.response_time_minutes),0)/totalInc):0;
  const opAssets=assets.filter(isOpAvail).length;
  const assetAvail=pct(opAssets,assets.length);
  const totalPart=trainings.reduce((s,t)=>s+n(t.participants_count),0);
  const totalComp=trainings.reduce((s,t)=>s+n(t.completions_count),0);
  const certRate=pct(totalComp,totalPart);
  const totalSavings=improvements.reduce((s,i)=>s+n(i.savings_realized_fcfa),0);
  const avgQuality=improvements.length?Math.min(100,Math.round(improvements.reduce((s,i)=>s+n(i.quality_impact_score),0)/improvements.length)):0;
  const pendingInc=incidents.filter(i=>['Ouvert','En cours','En attente de validation'].includes(i.status)).length;

  const trendData=useMemo(()=>{
    const m:Record<string,{total:number;resolved:number;critical:number}>={};
    incidents.forEach(i=>{const ym=String(i.incident_date).slice(0,7);if(!m[ym])m[ym]={total:0,resolved:0,critical:0};m[ym].total++;if(isResolved(i))m[ym].resolved++;if(isCritical(i))m[ym].critical++;});
    return Object.entries(m).sort().slice(-12).map(([month,d])=>({month,total:d.total,resolved:d.resolved,critical:d.critical,taux:Math.round(d.resolved/Math.max(d.total,1)*100)}));
  },[incidents]);

  const deltas:Record<string,number>={nsi:+3.2,resRate:+2.1,assetAvail:-1.4,certRate:+5.8,savings:+12.3,avgResp:-8.0};
  const fu={initial:{opacity:0,y:20},animate:{opacity:1,y:0},transition:{duration:.5}};

  // ── Skeleton loader ──
  if(loading) return(
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative">
          <Shield className="w-10 h-10 text-blue-400"/>
          <motion.div className="absolute inset-0 rounded-full bg-blue-400/20" animate={{scale:[1,1.5,1],opacity:[.5,0,.5]}} transition={{duration:1.5,repeat:Infinity}}/>
        </div>
        <div><Skeleton baseColor="#1e293b" highlightColor="#334155" width={240} height={24}/><Skeleton baseColor="#1e293b" highlightColor="#334155" width={180} height={14} className="mt-1"/></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{Array(6).fill(0).map((_,i)=><SkeletonCard key={i}/>)}</div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4"><SkeletonCard/><SkeletonCard/><SkeletonCard/></div>
      <div className="text-center text-slate-500 text-sm flex items-center justify-center gap-2 pt-4">
        <RefreshCw className="w-4 h-4 animate-spin text-blue-400"/>Chargement du tableau de bord exécutif…
      </div>
    </div>
  );

  return(
    <div className="p-4 md:p-6 space-y-5" style={{background:'linear-gradient(180deg,rgba(2,6,23,0.3) 0%,transparent 60%)'}}>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <motion.div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center"
              initial={{rotate:-180,opacity:0}} animate={{rotate:0,opacity:1}} transition={{duration:.8,ease:'backOut'}}>
              <Shield className="w-5 h-5 text-blue-400"/>
            </motion.div>
            <div>
              <h1 className="text-2xl font-bold text-white">Tableau de Bord Exécutif</h1>
              <motion.p className="text-slate-400 text-xs overflow-hidden whitespace-nowrap"
                initial={{width:0}} animate={{width:'auto'}} transition={{duration:1.4,ease:'easeOut',delay:.3}}>
                Direction Générale de la Sûreté Nationale — Vue 360°
              </motion.p>
            </div>
            <motion.span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-500/20 text-red-300 border border-red-500/30 ml-2"
              animate={{opacity:[1,.4,1]}} transition={{duration:1.5,repeat:Infinity}}>CONFIDENTIEL</motion.span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <motion.div className={`w-2 h-2 rounded-full ${refreshing?'bg-blue-400':'bg-green-400'}`}
              animate={refreshing?{opacity:[1,.3,1]}:{opacity:[1,.6,1],scale:[1,1.3,1]}} transition={{duration:1,repeat:Infinity}}/>
            {refreshing?'Actualisation…':`Mis à jour: ${lastUpd.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}`}
          </div>
          <button onClick={fetchAll} disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300 text-xs transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing?'animate-spin':''}`}/>Actualiser
          </button>
        </div>
      </div>

      {/* TIER 1: KPI Cards with CountUp */}
      <motion.div {...fu} transition={{duration:.5,delay:.05}} className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KPICard icon={DollarSign}  label="Économies LEAN (M F)"      numValue={Math.round(totalSavings/1e6*10)/10} suffix="M F" decimals={1} delta={deltas.savings} color="text-yellow-400" bg="bg-yellow-500/10" border="border-yellow-500/30"/>
        <KPICard icon={TrendingUp}  label="Taux de résolution"         numValue={resRate}      suffix="%"   decimals={1} delta={deltas.resRate}   color="text-green-400"  bg="bg-green-500/10"  border="border-green-500/30"/>
        <KPICard icon={Activity}    label="Délai moyen réponse"        numValue={avgResp}      suffix=" mn"              delta={deltas.avgResp}   color="text-cyan-400"   bg="bg-cyan-500/10"   border="border-cyan-500/30"/>
      </motion.div>

      {/* TIER 2A: NSI Gauge + Alerts + Radial */}
      <motion.div {...fu} transition={{duration:.5,delay:.2}} className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* NSI */}
        <motion.div whileHover={{boxShadow:'0 0 24px rgba(59,130,246,0.08)'}}
          className="rounded-xl border border-blue-900/40 overflow-hidden shadow-lg flex flex-col"
          style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
            style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
            <Shield className="w-4 h-4 text-blue-400"/>
            <span className="text-sm font-semibold text-white">NSI Gauge — Score composite pondéré (0-100)</span>
          </div>
          <div className="p-4 flex flex-col items-center">
            <NSIGauge score={nsi}/>
            <div className="grid grid-cols-3 gap-2 mt-6 w-full">
              {[
                {label:'Incidents',value:totalInc,color:'text-blue-400'},
                {label:'Critiques',value:criticalInc,color:'text-red-400',alert:criticalInc>0},
                {label:'En attente',value:pendingInc,color:'text-orange-400'},
              ].map((s,i)=>(
                <motion.div key={i} whileHover={{scale:1.05}}
                  className={`rounded-lg p-3 text-center ${s.alert?'bg-red-500/10 border border-red-500/20':'bg-slate-700/60'}`}>
                  <motion.p className={`font-bold text-base ${s.color}`}
                    initial={{scale:0,opacity:0}} animate={{scale:1,opacity:1}} transition={{type:'spring',stiffness:200,damping:15,delay:.8+i*.15}}>
                    <CountUp end={s.value} duration={1.2} preserveValue/>
                  </motion.p>
                  <p className="text-slate-500 text-xs">{s.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* 4 Gauges card — 2/3 width */}
        <div className="lg:col-span-2 rounded-xl border border-blue-900/40 overflow-hidden shadow-lg"
          style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
            style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
            <Target className="w-4 h-4 text-purple-400"/>
            <span className="text-sm font-semibold text-white">Tableaux de bord — Scores opérationnels</span>
          </div>
          <div className="grid grid-cols-2 gap-2 p-2">

            {/* Score Global de Risque — from Operations/Incidents */}
            <div className="flex flex-col items-center p-2 rounded-xl border border-blue-900/30" style={{background:"rgba(15,23,42,0.6)"}}>
              <p className="text-xs font-semibold text-slate-400 mb-1 text-center">Score Global de Risque</p>
              {(()=>{
                const score=Math.min(100,Math.max(resRate>0?Math.round(100-resRate):50,0));
                const color=score>=70?'#ef4444':score>=50?'#f97316':score>=30?'#eab308':'#10b981';
                const label=score>=70?'CRITIQUE':score>=50?'ÉLEVÉ':score>=30?'MODÉRÉ':'FAIBLE';
                const r=70,cx=88,cy=92;
                const toR=(d:number)=>d*Math.PI/180;
                const px=(d:number)=>cx+r*Math.cos(toR(d));
                const py=(d:number)=>cy-r*Math.sin(toR(d));
                const needleDeg=180-(score/100)*180;
                const nx=cx+(r-16)*Math.cos(toR(needleDeg));
                const ny=cy-(r-16)*Math.sin(toR(needleDeg));
                return(<svg width="150" height="124" viewBox="0 0 176 155">
                  <defs><linearGradient id="exRiskGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#10b981"/><stop offset="30%" stopColor="#eab308"/>
                    <stop offset="60%" stopColor="#f97316"/><stop offset="100%" stopColor="#ef4444"/>
                  </linearGradient></defs>
                  <path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(0)} ${py(0)}`} fill="none" stroke="#1e293b" strokeWidth="12" strokeLinecap="round"/>
                  {score>0&&<motion.path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(needleDeg)} ${py(needleDeg)}`}
                    fill="none" stroke="url(#exRiskGrad)" strokeWidth="12" strokeLinecap="round"
                    initial={{pathLength:0,opacity:0}} animate={{pathLength:1,opacity:1}} transition={{duration:2,ease:'easeOut'}}/>}
                  {[0,25,50,75,100].map(v=>{const deg=180-(v/100)*180;const x1=cx+(r-17)*Math.cos(toR(deg)),y1=cy-(r-17)*Math.sin(toR(deg));const x2=cx+(r-6)*Math.cos(toR(deg)),y2=cy-(r-6)*Math.sin(toR(deg));return <line key={v} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#334155" strokeWidth="1.5"/>;})}
                  <motion.line x1={cx} y1={cy} x2={cx} y2={cy-(r-16)} animate={{x2:nx,y2:ny}} transition={{type:'spring',stiffness:50,damping:15,delay:.6}} stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx={cx} cy={cy} r="5" fill="white"/>
                  <text x={cx} y={cy+28} textAnchor="middle" fill="white" fontSize="20" fontWeight="bold">{score.toFixed(1)}</text>
                  <text x={cx} y={cy+50} textAnchor="middle" fill={color} fontSize="13" fontWeight="bold">{label}</text>
                  <text x="12" y="108" fill="#10b981" fontSize="7">0</text><text x="156" y="108" fill="#ef4444" fontSize="7">100</text>
                </svg>);
              })()}
            </div>

            {/* Score Qualité Global — from LEAN */}
            <div className="flex flex-col items-center p-2 rounded-xl border border-blue-900/30" style={{background:"rgba(15,23,42,0.6)"}}>
              <p className="text-xs font-semibold text-slate-400 mb-1 text-center">Score Qualité Global</p>
              {(()=>{
                const score=Math.min(100,Math.max(avgQuality,0));
                const sigmaLvl=score>=95?5:score>=90?4:score>=85?3:score>=75?2:1;
                const color=score>=90?'#10b981':score>=75?'#3b82f6':score>=60?'#eab308':'#ef4444';
                const label=score>=90?'EXCELLENT':score>=75?'BON':score>=60?'MODÉRÉ':'CRITIQUE';
                const r=70,cx=88,cy=92;
                const toR=(d:number)=>d*Math.PI/180;
                const px=(d:number)=>cx+r*Math.cos(toR(d));
                const py=(d:number)=>cy-r*Math.sin(toR(d));
                const needleDeg=180-(score/100)*180;
                const nx=cx+(r-16)*Math.cos(toR(needleDeg));
                const ny=cy-(r-16)*Math.sin(toR(needleDeg));
                return(<svg width="150" height="124" viewBox="0 0 176 155">
                  <defs><linearGradient id="exSgGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#ef4444"/><stop offset="40%" stopColor="#eab308"/>
                    <stop offset="70%" stopColor="#3b82f6"/><stop offset="100%" stopColor="#10b981"/>
                  </linearGradient></defs>
                  <path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(0)} ${py(0)}`} fill="none" stroke="#1e293b" strokeWidth="12" strokeLinecap="round"/>
                  {score>0&&<motion.path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(needleDeg)} ${py(needleDeg)}`}
                    fill="none" stroke="url(#exSgGrad)" strokeWidth="12" strokeLinecap="round"
                    initial={{pathLength:0,opacity:0}} animate={{pathLength:1,opacity:1}} transition={{duration:2,ease:'easeOut'}}/>}
                  {[0,25,50,75,100].map(v=>{const deg=180-(v/100)*180;const x1=cx+(r-17)*Math.cos(toR(deg)),y1=cy-(r-17)*Math.sin(toR(deg));const x2=cx+(r-6)*Math.cos(toR(deg)),y2=cy-(r-6)*Math.sin(toR(deg));return <line key={v} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#334155" strokeWidth="1.5"/>;})}
                  <motion.line x1={cx} y1={cy} x2={cx} y2={cy-(r-16)} animate={{x2:nx,y2:ny}} transition={{type:'spring',stiffness:50,damping:15,delay:.6}} stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx={cx} cy={cy} r="5" fill="white"/>
                  <text x={cx} y={cy+28} textAnchor="middle" fill="white" fontSize="20" fontWeight="bold">{score}</text>
                  <text x={cx} y={cy+50} textAnchor="middle" fill={color} fontSize="13" fontWeight="bold">{sigmaLvl}σ — {label}</text>
                  <text x="12" y="108" fill="#ef4444" fontSize="7">0</text><text x="156" y="102" fill="#10b981" fontSize="7">100</text>
                </svg>);
              })()}
            </div>

            {/* Capacité Opérationnelle — from Formation */}
            <div className="flex flex-col items-center p-2 rounded-xl border border-blue-900/30" style={{background:"rgba(15,23,42,0.6)"}}>
              <p className="text-xs font-semibold text-slate-400 mb-1 text-center">Capacité Opérationnelle</p>
              {(()=>{
                const score=Math.min(100,Math.max(certRate,0));
                const color=score>=80?'#10b981':score>=60?'#3b82f6':score>=40?'#eab308':'#ef4444';
                const label=score>=80?'EXCELLENT':score>=60?'BON':score>=40?'MOYEN':'CRITIQUE';
                const r=70,cx=88,cy=92;
                const toR=(d:number)=>d*Math.PI/180;
                const px=(d:number)=>cx+r*Math.cos(toR(d));
                const py=(d:number)=>cy-r*Math.sin(toR(d));
                const needleDeg=180-(score/100)*180;
                const nx=cx+(r-16)*Math.cos(toR(needleDeg));
                const ny=cy-(r-16)*Math.sin(toR(needleDeg));
                return(<svg width="150" height="124" viewBox="0 0 176 155">
                  <defs><linearGradient id="exCapGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#ef4444"/><stop offset="40%" stopColor="#eab308"/>
                    <stop offset="70%" stopColor="#3b82f6"/><stop offset="100%" stopColor="#10b981"/>
                  </linearGradient></defs>
                  <path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(0)} ${py(0)}`} fill="none" stroke="#1e293b" strokeWidth="12" strokeLinecap="round"/>
                  {score>0&&<motion.path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(needleDeg)} ${py(needleDeg)}`}
                    fill="none" stroke="url(#exCapGrad)" strokeWidth="12" strokeLinecap="round"
                    initial={{pathLength:0,opacity:0}} animate={{pathLength:1,opacity:1}} transition={{duration:2,ease:'easeOut'}}/>}
                  {[0,25,50,75,100].map(v=>{const deg=180-(v/100)*180;const x1=cx+(r-17)*Math.cos(toR(deg)),y1=cy-(r-17)*Math.sin(toR(deg));const x2=cx+(r-6)*Math.cos(toR(deg)),y2=cy-(r-6)*Math.sin(toR(deg));return <line key={v} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#334155" strokeWidth="1.5"/>;})}
                  <motion.line x1={cx} y1={cy} x2={cx} y2={cy-(r-16)} animate={{x2:nx,y2:ny}} transition={{type:'spring',stiffness:50,damping:15,delay:.6}} stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx={cx} cy={cy} r="5" fill="white"/>
                  <text x={cx} y={cy+24} textAnchor="middle" fill="white" fontSize="18" fontWeight="bold">{score.toFixed(1)}%</text>
                  <text x={cx} y={cy+40} textAnchor="middle" fill={color} fontSize="11" fontWeight="bold">{label}</text>
                  <text x="12" y="108" fill="#ef4444" fontSize="7">0%</text><text x="152" y="102" fill="#10b981" fontSize="7">100%</text>
                </svg>);
              })()}
            </div>

            {/* Score Santé de la Flotte — from Logistics */}
            <div className="flex flex-col items-center p-2 rounded-xl border border-blue-900/30" style={{background:"rgba(15,23,42,0.6)"}}>
              <p className="text-xs font-semibold text-slate-400 mb-1 text-center">Score Santé/Disponibilité Flotte</p>
              {(()=>{
                const score=Math.min(100,Math.max(assetAvail,0));
                const color=score>=70?'#10b981':score>=50?'#3b82f6':score>=30?'#eab308':'#ef4444';
                const label=score>=70?'EXCELLENT':score>=50?'BON':score>=30?'MOYEN':'CRITIQUE';
                const r=70,cx=88,cy=92;
                const toR=(d:number)=>d*Math.PI/180;
                const px=(d:number)=>cx+r*Math.cos(toR(d));
                const py=(d:number)=>cy-r*Math.sin(toR(d));
                const needleDeg=180-(score/100)*180;
                const nx=cx+(r-16)*Math.cos(toR(needleDeg));
                const ny=cy-(r-16)*Math.sin(toR(needleDeg));
                return(<svg width="150" height="124" viewBox="0 0 176 155">
                  <defs><linearGradient id="exFleetGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#ef4444"/><stop offset="40%" stopColor="#eab308"/>
                    <stop offset="70%" stopColor="#3b82f6"/><stop offset="100%" stopColor="#10b981"/>
                  </linearGradient></defs>
                  <path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(0)} ${py(0)}`} fill="none" stroke="#1e293b" strokeWidth="12" strokeLinecap="round"/>
                  {score>0&&<motion.path d={`M${px(180)} ${py(180)} A${r} ${r} 0 0 1 ${px(needleDeg)} ${py(needleDeg)}`}
                    fill="none" stroke="url(#exFleetGrad)" strokeWidth="12" strokeLinecap="round"
                    initial={{pathLength:0,opacity:0}} animate={{pathLength:1,opacity:1}} transition={{duration:2,ease:'easeOut'}}/>}
                  {[0,25,50,75,100].map(v=>{const deg=180-(v/100)*180;const x1=cx+(r-17)*Math.cos(toR(deg)),y1=cy-(r-17)*Math.sin(toR(deg));const x2=cx+(r-6)*Math.cos(toR(deg)),y2=cy-(r-6)*Math.sin(toR(deg));return <line key={v} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#334155" strokeWidth="1.5"/>;})}
                  <motion.line x1={cx} y1={cy} x2={cx} y2={cy-(r-16)} animate={{x2:nx,y2:ny}} transition={{type:'spring',stiffness:50,damping:15,delay:.6}} stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx={cx} cy={cy} r="5" fill="white"/>
                  <text x={cx} y={cy+24} textAnchor="middle" fill="white" fontSize="18" fontWeight="bold">{score.toFixed(1)}%</text>
                  <text x={cx} y={cy+40} textAnchor="middle" fill={color} fontSize="11" fontWeight="bold">{label}</text>
                  <text x="12" y="108" fill="#ef4444" fontSize="7">0</text><text x="156" y="102" fill="#10b981" fontSize="7">100</text>
                </svg>);
              })()}
            </div>

          </div>
        </div>
      </motion.div>

      {/* TIER 2B: Map + Region Table */}
      <motion.div {...fu} transition={{duration:.5,delay:.25}} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div className="rounded-xl border border-blue-900/40 overflow-hidden shadow-lg"
          style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}
          animate={{boxShadow:['0 0 0px rgba(59,130,246,0)','0 0 20px rgba(59,130,246,0.12)','0 0 0px rgba(59,130,246,0)']}}
          transition={{duration:3,repeat:Infinity,ease:'easeInOut'}}>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
            style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
            <MapPin className="w-4 h-4 text-green-400"/>
            <span className="text-sm font-semibold text-white flex-1">Carte de commandement nationale — Risques par région</span>
          </div>
          <div className="p-3"><CommandMap incidents={incidents} assets={assets}/></div>
        </motion.div>
        <GlassCard icon={BarChart2} title="Classement régional — Score composite" accent="text-blue-400">
          <div className="overflow-y-auto" style={{maxHeight:'420px'}}>
            <RegionPerf incidents={incidents} assets={assets} trainings={trainings}/>
          </div>
        </GlassCard>
      </motion.div>

      {/* TIER 2C: Trend */}
      <motion.div initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{duration:.7,delay:.3}}>
        <div className="rounded-xl border border-blue-900/40 overflow-hidden shadow-lg relative"
          style={{background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)',backdropFilter:'blur(8px)'}}>
          <style>{`@keyframes scanline{0%{top:-2px}100%{top:102%}}.scanline{position:absolute;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(59,130,246,.4),transparent);animation:scanline 4s linear infinite;pointer-events:none;z-index:10;}`}</style>
          <div className="scanline"/>
          <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
            style={{background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)'}}>
            <TrendingUp className="w-4 h-4 text-cyan-400"/>
            <span className="text-sm font-semibold text-white flex-1">Tendance sécuritaire — 12 derniers mois</span>
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-blue-400 inline-block"/>Total</span>
              <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-green-400 inline-block"/>Résolus</span>
              <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-red-400 inline-block"/>Critiques</span>
            </div>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={trendData} margin={{top:8,right:8,left:8,bottom:24}}>
                <defs>
                  <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={.25}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/></linearGradient>
                  <linearGradient id="resolvedGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={.25}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f"/>
                <XAxis dataKey="month" tick={{fill:'#94a3b8',fontSize:9}} label={{value:'Année-Mois',position:'insideBottom',offset:-14,fill:'#64748b',fontSize:9}}/>
                <YAxis yAxisId="left" tick={{fill:'#94a3b8',fontSize:9}} label={{value:'Total | Résolus | Critiques',angle:-90,position:'insideLeft',fill:'#64748b',fontSize:9,dx:12}}/>
                <YAxis yAxisId="right" orientation="right" domain={[0,100]} tick={{fill:'#94a3b8',fontSize:9}} tickFormatter={v=>`${v}%`} label={{value:'Taux de Résolution',angle:90,position:'insideRight',fill:'#64748b',fontSize:9,dx:-8}}/>
                <Tooltip content={<NexTip/>}/>
                <Legend wrapperStyle={{fontSize:'9px'}} verticalAlign="top" align="right"/>
                <Area yAxisId="left" type="monotone" dataKey="total" name="Total incidents" stroke="#3b82f6" strokeWidth={2} fill="url(#totalGrad)" isAnimationActive animationDuration={1500}/>
                <Area yAxisId="left" type="monotone" dataKey="resolved" name="Résolus" stroke="#10b981" strokeWidth={2} fill="url(#resolvedGrad)" isAnimationActive animationDuration={1700}/>
                <Bar yAxisId="left" dataKey="critical" name="Critiques" fill="#ef4444" opacity={.7} isAnimationActive animationDuration={1200}/>
                <Line yAxisId="right" type="monotone" dataKey="taux" name="Taux résolution (%)" stroke="#a855f7" strokeWidth={2} strokeDasharray="4 2" dot={false} isAnimationActive animationDuration={2000}/>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>

      {/* TIER 3: Obligations + LEAN + Formation */}
      <motion.div initial={{opacity:0,y:24}} animate={{opacity:1,y:0}} transition={{duration:.6,delay:.35}} className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Obligations */}
        <GlassCard icon={FileText} title="Obligations réglementaires" accent="text-yellow-400">
          <div className="space-y-2">
            {[
              {name:'Rapport Trimestriel',due:'31 déc.',days:33,color:'#10b981'},
              {name:'Bilan Primature',due:'30 nov.',days:-2,color:'#ef4444'},
              {name:'TOFE Mensuel',due:'15 nov.',days:-15,color:'#ef4444'},
              {name:'Liasses Cour des Comptes',due:'31 déc.',days:33,color:'#10b981'},
              {name:'Rapport Interpol',due:'31 déc.',days:33,color:'#10b981'},
            ].map((o,i)=>(
              <motion.div key={i} initial={{opacity:0,x:-8}} animate={{opacity:1,x:0}} transition={{delay:i*.08}}
                whileHover={{scale:1.01}}
                className="flex items-center gap-3 p-2 rounded-lg bg-slate-700/40">
                <motion.div className="w-1.5 rounded-full flex-shrink-0"
                  initial={{scaleY:0}} animate={{scaleY:1}} transition={{duration:.5,delay:i*.1,ease:'backOut'}}
                  style={{height:'32px',background:o.color}}/>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium truncate">{o.name}</p>
                  <p className="text-slate-500 text-xs">{o.due}</p>
                </div>
                <motion.span className="text-xs font-bold flex-shrink-0" style={{color:o.color}}
                  animate={o.days<0?{opacity:[1,.5,1]}:{}} transition={{duration:1.2,repeat:Infinity}}>
                  {o.days<0?`${Math.abs(o.days)}j retard`:`${o.days}j`}
                </motion.span>
              </motion.div>
            ))}
          </div>
        </GlassCard>

        {/* LEAN */}
        <GlassCard icon={Zap} title="Performance LEAN Six Sigma" accent="text-green-400">
          <div className="space-y-3">
            {[
              {label:'Économies réalisées',numVal:Math.round(totalSavings/1e6*10)/10,suffix:'M F',decimals:1,color:'text-green-400',icon:DollarSign},
              {label:'Score qualité global',numVal:avgQuality,suffix:'/100',color:'text-blue-400',icon:Star},
              {label:'Améliorations déployées',numVal:improvements.length,color:'text-purple-400',icon:TrendingUp},
              {label:'Unités adoptantes (moy.)',numVal:improvements.length?Math.round(improvements.reduce((s,i)=>s+n(i.quality_impact_score),0)/improvements.length*10)/10:0,decimals:1,color:'text-cyan-400',icon:Users},
            ].map((s,i)=>(
              <motion.div key={i} initial={{opacity:0,x:8}} animate={{opacity:1,x:0}} transition={{delay:i*.08}}
                whileHover={{scale:1.01,backgroundColor:'rgba(59,130,246,0.05)'}}
                className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-700/40 transition-colors">
                <s.icon className={`w-4 h-4 ${s.color} flex-shrink-0`}/>
                <span className="text-slate-300 text-xs flex-1">{s.label}</span>
                <span className={`font-bold text-sm ${s.color}`}>
                  <CountUp end={s.numVal} duration={1.2} decimals={s.decimals||0} suffix={s.suffix||''} preserveValue/>
                </span>
              </motion.div>
            ))}
          </div>
        </GlassCard>

        {/* Formation */}
        <GlassCard icon={Award} title="Capacités opérationnelles" accent="text-purple-400">
          <div className="space-y-3">
            {[
              {label:'Agents formés (total)',numVal:totalComp,color:'text-green-400',icon:Users},
              {label:"Taux d'achèvement",numVal:certRate,suffix:'%',decimals:1,color:'text-blue-400',icon:CheckCircle},
              {label:'Programmes actifs',numVal:trainings.length,color:'text-purple-400',icon:Award},
              {label:'Investissement formation',numVal:Math.round(trainings.reduce((s,t)=>s+n(t.total_cost),0)/1e6*10)/10,suffix:'M F',decimals:1,color:'text-yellow-400',icon:DollarSign},
            ].map((s,i)=>(
              <motion.div key={i} initial={{opacity:0,x:8}} animate={{opacity:1,x:0}} transition={{delay:i*.08}}
                whileHover={{scale:1.01,backgroundColor:'rgba(59,130,246,0.05)'}}
                className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-700/40 transition-colors">
                <s.icon className={`w-4 h-4 ${s.color} flex-shrink-0`}/>
                <span className="text-slate-300 text-xs flex-1">{s.label}</span>
                <span className={`font-bold text-sm ${s.color}`}>
                  <CountUp end={s.numVal} duration={1.2} decimals={s.decimals||0} suffix={s.suffix||''} preserveValue/>
                </span>
              </motion.div>
            ))}
          </div>
        </GlassCard>
      </motion.div>

      {/* Footer */}
      <div className="rounded-xl border border-blue-900/30 px-4 py-3 flex flex-wrap items-center justify-between gap-2"
        style={{background:'linear-gradient(135deg,rgba(15,23,42,0.7) 0%,rgba(30,41,59,0.4) 100%)'}}>
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-blue-400"/>
          <span className="text-slate-400 text-xs italic">Tableau de Bord Exécutif DGSN — Usage exclusif Direction Générale — CONFIDENTIEL</span>
        </div>
        <span className="text-blue-300 text-xs font-medium">NEXUS360 · Données souveraines · Cameroun</span>
      </div>
    </div>
  );
};
